import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Container, Form, Modal, Spinner, Table } from "react-bootstrap";
import API from "../../services/api";
import { fmtDate, normalizeRows, orDash } from "./AccountsShared";
import "../EmployeeAccounts.css";

const uploadPerms = ["finance:vendor-bill:upload"];
const approvePerms = ["finance:vendor-bill:approve"];

const currency = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const statusTone = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("approve")) return "approved";
  if (value.includes("paid")) return "paid";
  if (value.includes("reject")) return "rejected";
  return "pending";
};

export const AccountsVendorBills = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({ search: "", status: "", vendor: "" });
  const [form, setForm] = useState({
    billNo: "",
    vendor: "",
    amount: "",
    attachment: "",
  });

  const canCreate = uploadPerms.some((p) => perms.includes(p));
  const canApprove = approvePerms.some((p) => perms.includes(p));

  const fetchRows = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get("/employee/accounts/vendor-bills");
      setRows(normalizeRows(res.data));
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load vendor bills");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await API.get("/auth/me");
        const p = res.data?.employeeRole?.permissions || [];
        setPerms(Array.isArray(p) ? p : []);
      } catch {
        setPerms([]);
      }
    };
    fetchPerms();
  }, []);

  const vendorOptions = useMemo(() => {
    const names = rows
      .map((row) => row.vendor?.name || row.vendor)
      .filter((value) => typeof value === "string" && value.trim());
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return rows.filter((row) => {
      const vendorName = row.vendor?.name || row.vendor || "";
      const status = String(row.status || "");
      const matchesSearch =
        !search ||
        [row.billNo, vendorName, status]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(search);
      const matchesStatus =
        !filters.status || status.toLowerCase() === filters.status.toLowerCase();
      const matchesVendor = !filters.vendor || vendorName === filters.vendor;
      return matchesSearch && matchesStatus && matchesVendor;
    });
  }, [filters, rows]);

  const summary = useMemo(() => {
    const pending = rows.filter((row) => statusTone(row.status) === "pending");
    const approved = rows.filter((row) => statusTone(row.status) === "approved");
    const paid = rows.filter((row) => statusTone(row.status) === "paid");
    return {
      total: rows.length,
      pending: pending.length,
      approved: approved.length,
      paid: paid.length,
      pendingAmount: pending.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    };
  }, [rows]);

  const openCreate = () => {
    setForm({
      billNo: "",
      vendor: "",
      amount: "",
      attachment: "",
    });
    setShowCreate(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setErr("");
      await API.post("/employee/accounts/vendor-bills", {
        ...form,
        amount: Number(form.amount || 0),
      });
      setShowCreate(false);
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Bill upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      setErr("");
      await API.patch(`/employee/accounts/vendor-bills/${id}/status`, { status });
      fetchRows();
    } catch (e) {
      setErr(e?.response?.data?.message || "Status update failed");
    }
  };

  return (
    <Container className="accounts-page accounts-vendor-page">
      <section className="accounts-vendor-shell">
        <header className="accounts-vendor-topbar">
          <div className="accounts-vendor-brand">
            <div className="accounts-vendor-brandmark">L</div>
            <div>
              <h1>Lotus Rice Mill Pvt. Ltd.</h1>
              <p>Finance Portal · Vendor Management</p>
            </div>
          </div>
          <div className="accounts-vendor-user">
            <button type="button" className="accounts-vendor-iconbtn">
              <span className="material-symbols-outlined">notifications</span>
            </button>
            <div className="accounts-vendor-avatar">AD</div>
          </div>
        </header>

        <div className="accounts-vendor-body">
          <section className="accounts-vendor-pagehead">
            <div>
              <h2>Vendor Bills</h2>
              <p>Upload, review, and approve vendor invoices with ease.</p>
            </div>
            <div className="accounts-vendor-headactions">
              <Button variant="outline-secondary" onClick={fetchRows}>
                Refresh
              </Button>
              {canCreate && (
                <Button className="accounts-btn-primary" onClick={openCreate}>
                  Upload New Bill
                </Button>
              )}
            </div>
          </section>

          <section className="accounts-vendor-summary">
            <article>
              <span>Total Bills</span>
              <strong>{summary.total}</strong>
            </article>
            <article>
              <span>Pending Review</span>
              <strong>{summary.pending}</strong>
            </article>
            <article>
              <span>Approved</span>
              <strong>{summary.approved}</strong>
            </article>
            <article>
              <span>Pending Exposure</span>
              <strong>{currency(summary.pendingAmount)}</strong>
            </article>
          </section>

          <section className="accounts-vendor-filters">
            <div className="accounts-vendor-search">
              <span className="material-symbols-outlined">search</span>
              <input
                type="text"
                placeholder="Search by Bill No or Vendor..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <Form.Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </Form.Select>
            <Form.Select
              value={filters.vendor}
              onChange={(e) => setFilters({ ...filters, vendor: e.target.value })}
            >
              <option value="">All Vendors</option>
              {vendorOptions.map((vendor) => (
                <option key={vendor} value={vendor}>
                  {vendor}
                </option>
              ))}
            </Form.Select>
          </section>

          {err && <Alert variant="danger">{err}</Alert>}

          <section className="accounts-vendor-tablewrap">
            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                <div className="accounts-vendor-table-scroll">
                  <Table responsive className="accounts-vendor-table">
                    <thead>
                      <tr>
                        <th>Bill No</th>
                        <th>Vendor</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-5 text-muted">
                            No vendor bills found
                          </td>
                        </tr>
                      ) : (
                        filteredRows.map((row, index) => {
                          const tone = statusTone(row.status);
                          const attachmentUrl = row.attachment || row.attachmentUrl || row.fileUrl;
                          const currentStatus = String(row.status || "pending").toLowerCase();
                          const isApproved = currentStatus === "approved";
                          return (
                            <tr key={row._id || row.id || index}>
                              <td className="accounts-vendor-billno">{orDash(row.billNo)}</td>
                              <td>{orDash(row.vendor?.name || row.vendor)}</td>
                              <td>{fmtDate(row.createdAt)}</td>
                              <td className="accounts-vendor-amount">{currency(row.amount)}</td>
                              <td>
                                <span className={`accounts-vendor-status ${tone}`}>
                                  {orDash(row.status)}
                                </span>
                              </td>
                              <td className="text-end">
                                <div className="accounts-vendor-actions">
                                  <button
                                    type="button"
                                    title="View Attachment"
                                    disabled={!attachmentUrl}
                                    onClick={() => attachmentUrl && window.open(attachmentUrl, "_blank", "noopener,noreferrer")}
                                  >
                                    <span className="material-symbols-outlined">attach_file</span>
                                  </button>
                                  {canApprove && (
                                    <button
                                      type="button"
                                      title="Approve"
                                      disabled={isApproved}
                                      onClick={() => updateStatus(row._id, "approved")}
                                    >
                                      <span className="material-symbols-outlined">check_circle</span>
                                    </button>
                                  )}
                                  {canApprove && (
                                    <button
                                      type="button"
                                      title="Reject"
                                      disabled={currentStatus === "rejected"}
                                      onClick={() => updateStatus(row._id, "rejected")}
                                    >
                                      <span className="material-symbols-outlined">cancel</span>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>

                <footer className="accounts-vendor-foot">
                  <div>
                    Showing <strong>{filteredRows.length}</strong> of <strong>{rows.length}</strong> bills
                  </div>
                  <div className="accounts-vendor-footlinks">
                    <span>Support Desk</span>
                    <span>Internal Audit Docs</span>
                  </div>
                </footer>
              </>
            )}
          </section>
        </div>
      </section>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} fullscreen scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Upload Vendor Bill</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitCreate}>
            <Form.Group className="mb-3">
              <Form.Label>Bill No</Form.Label>
              <Form.Control
                value={form.billNo}
                onChange={(e) => setForm({ ...form, billNo: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Vendor</Form.Label>
              <Form.Control
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Attachment URL</Form.Label>
              <Form.Control
                value={form.attachment}
                onChange={(e) => setForm({ ...form, attachment: e.target.value })}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" className="accounts-btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save Bill"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};
